import { scrollToTop } from "Utils";
import { toast } from "react-toastify";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import { Grid, IconButton, Tooltip } from "@mui/material";
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import DepartmentServices from "services/DepartmentServices";
import React, { useCallback, useEffect, useState } from "react";
import {Button,CollapsibleSection,MuiTable,StatusChip,TextValidator,ValidatorForm,Box,ConfirmDialog,TableTopSearch,SingleViewDialog} from "components";

const initial_form = {
  organization: null,
  admin_name: null,
  email: null,
  phone: null,
  designation: null,
  status: "Active",
  userId: null,
  "order[0]": ["updatedAt", "DESC"],
};

const AddOGrgSetupDepartment = ({ org }) => {
  const [formData, setFormData] = useState(initial_form);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [filters, setFilters] = useState({
    page: 0,
    limit: 10,
    "order[0]": ["updatedAt", "DESC"],
    search: null,
  });
  const [totalItems, setTotalItems] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [singleData, setSingleData] = useState(null);
  
  const handleView = (row) => {
    setSingleData(row);
    setOpenDialog(true);
  };

  const getData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        organization_id: org?.id,
        search: filters.search,
        page: filters.page,
        limit: filters.limit,
        "order[0]": filters["order[0]"],
        
      };
      const response = await DepartmentServices.get(params);
      const total = response?.data?.view?.totalItems || response?.data?.totalItems || 0;
      setTotalItems(total);
      const departments = response?.data?.view?.data || response?.data?.data || [];
      setData(Array.isArray(departments) ? departments : []);
    } catch (error) {
      toast.error("Failed to fetch departments. Please try again.");
      setData([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [filters, org]);

  useEffect(() => {
    getData();
  }, [getData, org]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };


  const setPage = (page) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: page - 1,
    }));
  };

  const setPageSize = (pageSize) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: 0,
      limit: pageSize,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (!formData.admin_name || !formData.code || !formData.description) {
        toast.error("Department Name, Code, and Description are required!");
        setSubmitting(false);
        return;
      }

      const orgId = org?.id;
      if (!orgId) {
        toast.error("Organization is required!");
        setSubmitting(false);
        return;
      }

      const existingDepartmentWithCode = (data || []).find(
        (department) =>
          department.organization_id === orgId &&
          department.code?.toLowerCase() === formData.code?.toLowerCase() &&
          (!isEdit || department.id !== editingData)
      );

      if (existingDepartmentWithCode) {
        toast.error(`Department Code "${formData.code}" already exists for this organization!`);
        setSubmitting(false);
        return;
      }

      const apiData = {
        name: formData.admin_name,
        code: formData.code,
        description: formData.description,
        status: formData.status || "Active",
        organization_id: orgId,
      };

      let response;
      if (isEdit) {
        response = await DepartmentServices.updateByPatch(editingData, apiData);
        if (response.success) {
          toast.success("Department updated successfully!");
        } else {
          toast.error( "Failed to update. Please try again."
          );
        }
      } else {
        response = await DepartmentServices.create(apiData);
        if (response.success) {
          toast.success( "Department created successfully!");
        } else {
          toast.error("Failed to create. Please try again.");
        }
      }

      if (response.success) {
        setFormData(initial_form);
        setIsEdit(false);
        setOpenEditModal(false);
        setEditingData(null);
        getData();
        scrollToTop();
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
   
        <CollapsibleSection
          title="All departments"
          collapsTrigger={openEditModal}
          buttonText="Add new department"
        >
          <ValidatorForm onSubmit={handleSubmit} process={submitting}>
            <Grid container spacing={4}>
              <Grid size={{ lg: 6, md: 12, sm: 12, xs: 12 }} sx={{ mt: 2 }}>
                <Box>
                  <Grid container spacing={1}>
                    <Grid size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
                      <TextValidator
                        label="Department Name"
                        name="admin_name"
                        value={formData.admin_name || ""}
                        onChange={handleInputChange}
                        validators={["required"]}
                        errorMessages={["This field is required"]}
                        required={true}
                        disabled={loading}
                        placeholder="Enter department name"
                      />
                    </Grid>

                    
                    <Grid size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
                      <TextValidator
                        label="Department Code"
                        name="code"
                        value={formData.code || ""}
                        onChange={handleInputChange}
                        validators={["required"]}
                        errorMessages={["This field is required"]}
                        required={true}
                        disabled={loading}
                        placeholder="Department Code"
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid size={{ lg: 6, md: 12, sm: 12, xs: 12 }} sx={{ mt: 2 }}>
                <Box>
                  <Grid container spacing={2}>
                    <Grid size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
                      <TextValidator
                        label="Description"
                        name="description"
                        multiline
                        rows={4}
                        value={formData.description || ""}
                        onChange={handleInputChange}
                        validators={["required"]}
                        errorMessages={["This field is required"]}
                        required={true}
                        disabled={loading}
                        placeholder="Description"
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
            <Grid className="mt-2" container spacing={1}>
              <Grid>
                <Button type="submit" process={submitting}>
                  {isEdit ? "UPDATE" : "SAVE"}
                </Button>
              </Grid>
              <Grid>
                {openEditModal && (
                  <Button
                    className="btn-warning"
                    onClick={() => {
                      setFormData(initial_form);
                      setIsEdit(false);
                      setOpenEditModal(false);
                      setEditingData(null);
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </Grid>
            </Grid>
          </ValidatorForm>
        </CollapsibleSection>

        <div>
          <TableTopSearch
            title='Departments'
            showSearch={true}
            showTotalWithTitle={true}
            rowCount={totalItems}
            onSubmitSearch={null}
            onSearchChange={(e) => {
              setFilters(prevData => ({
                ...prevData,
                search: e.target.value
              }));
            }}
          />

          <MuiTable
            columns={[
              {
                accessorKey: "name",
                header: "Name",
                Cell: ({ row }) => <span>{row.original.name || ""}</span>,
              },
              {
                accessorKey: "code",
                header: "Department code",
                Cell: ({ row }) => <span>{row.original.code || ""}</span>,
              },
              {
                accessorKey: "status",
                header: "Status",
                Cell: ({ row }) => {
                  const handleChangeStatus = async (id, status) => {
                    const actionText = status === "Active"
                      ? "deactivate this department"
                      : "activate this department";
                    ConfirmDialog({
                      type: status === "Active" ? 'error' : 'success',
                      title: `Are you sure you want to ${actionText}?`,
                      description:
                        status === "Active"
                          ? "This will deactivate the department. You can reactivate it later."
                          : "This will activate the department. You can deactivate it later.",
                      buttons: [
                        {
                          label: 'No',
                          onClick: () => toast.info('Action cancelled.')
                        },
                        {
                          label: 'Yes',
                          onClick: async () => {
                            const newStatus = status === "Active" ? "Inactive" : "Active";
                            try {
                              const response = await DepartmentServices.updateStatusByPut(id, { status: newStatus });
                              if (response.success) {
                                toast.success(`Department status changed to ${newStatus}!`);
                                getData();
                              } else {
                                toast.error("Failed to update status.");
                              }
                            } catch (err) {
                              toast.error("Error updating status. Please try again.");
                            }
                          }
                        }
                      ]
                    });
                  };
                  return (
                    <span
                      style={{ cursor: 'pointer' }}
                      title={row.original.status === "Active" ? "Click to set Inactive" : "Click to set Active"}
                      onClick={e => {
                        e.stopPropagation();
                        handleChangeStatus(row.original.id, row.original.status);
                      }}
                    >
                      <StatusChip status={row.original.status || ""} />
                    </span>
                  );
                },
              },
              {
                accessorKey: "actions",
                header: "Actions",
                Cell: ({ cell }) => (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => {
                          setIsEdit(false);
                          setOpenEditModal(false);
                          setTimeout(() => {
                            const editData = {
                              ...cell.row.original,
                              admin_name: cell.row.original.name,
                              phone: cell.row.original.phone,
                              code: cell.row.original.code,
                              district: cell.row.original.district,
                            };
                            setFormData(editData);
                            setIsEdit(true);
                            setOpenEditModal(true);
                            setEditingData(cell.row.original.id);
                            scrollToTop();
                          }, 500);
                        }}
                      >
                        <DriveFileRenameOutlineOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleView(cell.row.original)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                  </div>
                ),
                headerProps: { style: { textAlign: "right" } },
                cellProps: { style: { textAlign: "right" } },
              },
            ]}
            data={data}
            title="All Departments"
            manualPagination={true}
            rowCount={totalItems}
            currentPage={filters.page}
            pageSize={filters.limit}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            loading={loading}
          />
        </div>
  
        <SingleViewDialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          data={{
            title: singleData?.name,
            otherData: [
                {
                label: "Status",
                value: singleData?.status
              },
              {
                label: "Description",
                value: singleData?.description
              }, 
              {
                label: "Department Code",
                value: singleData?.code
              },
                        

            ]
          }}
        />
    </div>
  );
};

export default AddOGrgSetupDepartment;
